/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import classNames from 'classnames/bind'
import { RouteComponentProps } from 'react-router-dom'
import { ROUTES } from 'constants/routes'
import { Field } from 'redux-form'
import { Text } from 'hocs/localization'
import Loading from 'apps/Loading'
import { Input, Checkbox } from 'apps/FormFields'
import ButtonWithIcon from 'apps/ButtonWithIcon'
import RenewalLeaseAgreement from 'apps/RenewalLeaseAgreement'
import Button from 'elements/Button'
import { SUBSCRIPTION_PLAN_TYPE } from 'constants/subscription'
import { toCurrency } from 'utils/price'
import contract from 'assets/images/contract.png'
import mo2mo from 'assets/images/mo2mo.png'
import { createValidation, createValidator, hasRequired, hasRequiredCheckbox } from 'utils/form'
import { FIELD_NAMES } from './constants'

import * as styles from './RenewalTerms.scss'

const cx = classNames.bind(styles)

interface IProps extends RouteComponentProps {
  t: Text,
  plan: any,
  summary: any,
  changePlan: () => void,
  downloadTerms: () => void,
  termsFileUrl: string | null,
  isLoading: boolean,
  submitting: boolean,
  valid: boolean,
  currency: string,
  businessName: string,
  userDetails: any,
  address: string,
  existingContractType: string,
  newContractType: string,
}

interface IState {
  isSubmitting: boolean,
  isTermsAccepted: boolean,
}

class RenewalTerms extends React.Component<IProps, IState> {
  public state = {
    isSubmitting: false,
    isTermsAccepted: false,
  }

  public requiredFieldValidation = createValidation([
    createValidator(hasRequired, this.props.getTextPath('errors.textField')),
  ])

  public requiredCheckboxValidation = createValidation([
    createValidator(hasRequiredCheckbox, ''),
  ])

  private onReturnBack = () => {
    this.props.history.push(ROUTES.profile.renewPlan.root)
  }

  private onChangePlan = formValues => {
    this.setState({
      isSubmitting: true,
    })

    this.props.changePlan(formValues).finally(() => {
      this.setState({
        isSubmitting: false,
      })
    })
  }

  public render () {
    const {
      t,
      data,
      isLoading,
      submitting,
      valid,
      handleSubmit,
      existingContractType,
      newContractType,
      userDetails,
      currency,
      businessName,
      address,
      plan,
    } = this.props

    const renderAgreement = () => {
      if (newContractType === SUBSCRIPTION_PLAN_TYPE.contract) {
        return (isLoading
          ? (<div className={styles.topSpace50}>
            <Loading />
          </div>)
          : (<RenewalLeaseAgreement {...data} />)
        )
      }
    }

    return (
      <div className={styles.root}>
        <div className='container'>
          <div className='row'>
            <div className={cx('col-md-12', 'text-center')}>
              <div className={styles.title}>
                <h2>{newContractType === SUBSCRIPTION_PLAN_TYPE.contract ? t('titleContract') : t('titleM2M')}</h2>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.ManageAccountWrapper}>
          <div className={styles.ManageAccount}>
            <div className={styles.planimg}>
              <img src={newContractType === SUBSCRIPTION_PLAN_TYPE.contract ? contract : mo2mo} alt={plan.name}/>
            </div>
            <div className={styles.planimgtext}>
              <ul>
                <li className={styles.planItem}>
                <span>
                  {t('planName')}:
                </span>
                  <span>
                  {plan.name}
                </span>
                </li>
                <li className={styles.planItem}>
                  <span>{t('contractType')}:</span>
                  <span>{newContractType === SUBSCRIPTION_PLAN_TYPE.contract ? 'Contract' : 'Mo-2-Mo'}</span>
                </li>
                <li className={styles.planItem}>
                <span>
                  {t('monthlyFee')}:
                </span>
                  <span>
                  {currency + toCurrency(data?.monthlyPayment)}
                </span>
                </li>
                {address && (
                  <li className={styles.planItem}>
                    <span>{t('address')}:</span>
                    <span>{address}</span>
                  </li>
                )}
                {businessName && (
                  <li className={styles.planItem}>
                    <span>{t('businessName')}:</span>
                    <span>{businessName}</span>
                  </li>
                )}
                {userDetails.creditCard && (
                  <li className={styles.planItem}>
                    <span>{t('cardOnFile')}:</span>
                    <div className={styles.cardData}>
                      <span>*{userDetails.creditCard.last4Digits}</span>
                      <span>{userDetails.creditCard.expirationMonth}/{userDetails.creditCard.expirationYear}</span>
                    </div>
                  </li>
                )}
                <li className={styles.planItem}>
                  <span>{t('emailAddress')}:</span>
                  <span>{userDetails.email}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className={cx('col-md-12', styles.topSpace50)}>

          {renderAgreement()}

          <div className={styles.terms}>
            <div className={styles.container1200}>
              <form className={styles.container768}>

              {newContractType === SUBSCRIPTION_PLAN_TYPE.contract && (
                <div className={styles.signForm}>
                  <div className={styles.signature}>
                    <div className={styles.companyName}>{data && data.businessName ? data.businessName : ''}</div>
                    <div className={styles.info}>{t('signaturePrompt')}</div>
                    <Field
                      disabled={submitting}
                      type='text'
                      name={FIELD_NAMES.customerSignature}
                      component={Input}
                      validate={this.requiredFieldValidation}
                    />
                    <div className={styles.signatureNote}>{t('signatureNote')}</div>
                    <div className={styles.info}>{data ? data.newContractSignDate : '' }</div>
                  </div>
                  <div className={styles.signature}>
                    <div className={styles.companyName}>{t('epsonName')}</div>
                    <div className={styles.info}/>
                    <Field
                      type='text'
                      component={Input}
                      name={FIELD_NAMES.epsonSignature}
                      placeholder={t('epsonName')}
                      disabled
                    />
                    <div className={styles.signatureNote}/>
                    <div className={styles.info}>{data ? data.newContractSignDate : '' }</div>
                  </div>
                </div>
              )}

              {newContractType === SUBSCRIPTION_PLAN_TYPE.contract && (
                <div className={styles.agreementCheckbox}>
                  <Field
                    disabled={submitting}
                    type='text'
                    name={FIELD_NAMES.termsRead}
                    component={Checkbox}
                    label={t('note')}
                    validate={this.requiredCheckboxValidation}
                  />
                </div>
              )}

              {newContractType === SUBSCRIPTION_PLAN_TYPE.monthToMonth && (
                <div className={styles.agreementCheckbox}>
                  <Field
                    disabled={submitting}
                    type='text'
                    name={FIELD_NAMES.termsRead}
                    component={Checkbox}
                    label={t('noteM2M')}
                    validate={this.requiredCheckboxValidation}
                  />
                </div>
              )}

                <div className={cx('row', styles.topSpace50)}>
                  <div className={styles.buttons}>
                    <div className={newContractType === SUBSCRIPTION_PLAN_TYPE.contract
                      ? styles.saveChangeContract : styles.saveChange}>
                      <div className={newContractType === SUBSCRIPTION_PLAN_TYPE.contract
                        ? styles.buttonWrapperContract : styles.buttonWrapper}>
                        <Button
                          disabled={this.state.isSubmitting ||
                            (isLoading && newContractType === SUBSCRIPTION_PLAN_TYPE.contract) ||
                            submitting || !valid}
                          onClick={handleSubmit(this.onChangePlan)}
                          fluid
                        >
                        {this.state.isSubmitting ? t('submitButtonSubmitting') :
                          (newContractType === SUBSCRIPTION_PLAN_TYPE.contract ? t('submitButtonContract') : t('submitButton'))}
                        </Button>
                      </div>

                      <div className={newContractType === SUBSCRIPTION_PLAN_TYPE.contract
                        ? styles.backLinkContract : styles.backLink}>
                        <ButtonWithIcon
                          styleType='noStyle'
                          iconName='arrowLeft'
                          onClick={this.onReturnBack}
                        >
                          {t('back')}
                        </ButtonWithIcon>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={cx('row', styles.utc)}>
                  {t('utc')}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default RenewalTerms
